import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Swords, Loader2, CheckCircle2, XCircle, Zap, Coins } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fireBigConfetti, fireConfetti } from "@/lib/confetti";
import { showXPToast } from "@/lib/xpToast";

export function BossBattleModal({ day, onClose }) {
  const { refreshUser } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/checkpoints/${day}`)
      .then((res) => setQuiz(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load Boss Battle"))
      .finally(() => setLoading(false));
  }, [day]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/checkpoints/${day}/submit`, { answers });
      setResult(data);
      if (data.passed) {
        data.bonus_awarded ? fireBigConfetti() : fireConfetti();
        if (data.bonus_awarded) showXPToast(data.bonus_xp, data.bonus_coins, "Boss Battle Won!");
        refreshUser();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit Boss Battle");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="boss-battle-modal">
      <div className="glass-panel rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6 relative border border-violet-400/30">
        <button onClick={onClose} data-testid="boss-battle-close-btn" className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Swords className="w-5 h-5 text-violet-400" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-violet-400">Boss Battle — Day {day} Checkpoint</span>
        </div>
        <h2 className="font-heading text-2xl font-semibold text-white mb-1">Review Quiz</h2>
        <p className="text-sm text-slate-400 mb-5">Score {quiz?.pass_score || 70}%+ for a bonus of +{quiz?.bonus_xp ?? 40} XP and +{quiz?.bonus_coins ?? 15} coins. This is optional and won't lock your main progress.</p>

        {loading && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>}

        {!loading && quiz && !result && (
          <div className="space-y-5">
            {quiz.questions.map((q) => (
              <div key={q.index} data-testid={`boss-battle-question-${q.index}`}>
                <p className="text-sm text-white font-medium mb-2">{q.index + 1}. {q.question}</p>
                <div className="space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <button
                      key={oi}
                      onClick={() => setAnswers((a) => ({ ...a, [q.index]: oi }))}
                      data-testid={`boss-battle-option-${q.index}-${oi}`}
                      className={`w-full text-left text-sm px-3.5 py-2.5 rounded-lg border transition-colors ${
                        answers[q.index] === oi ? "border-violet-400 bg-violet-400/10 text-white" : "border-white/10 text-slate-300 hover:border-white/20"
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button
              onClick={submit} disabled={submitting || Object.keys(answers).length < quiz.questions.length}
              data-testid="boss-battle-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Swords className="w-4 h-4" />}
              Submit Boss Battle
            </button>
          </div>
        )}

        {result && (
          <div className="text-center py-6" data-testid="boss-battle-result">
            {result.passed ? <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" /> : <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />}
            <h3 className="font-heading text-xl font-semibold text-white">{result.passed ? "Boss Defeated!" : "Not Quite — Try Again"}</h3>
            <p className="text-sm text-slate-400 mt-1">You scored {result.score}% ({result.correct_count}/{result.total} correct)</p>
            {result.bonus_awarded && (
              <div className="flex items-center justify-center gap-4 mt-4">
                <span className="flex items-center gap-1 text-sm font-semibold text-[#FBBF24]"><Zap className="w-4 h-4" /> +{result.bonus_xp} XP</span>
                <span className="flex items-center gap-1 text-sm font-semibold text-[#FBBF24]"><Coins className="w-4 h-4" /> +{result.bonus_coins} Coins</span>
              </div>
            )}
            <button onClick={onClose} data-testid="boss-battle-done-btn" className="mt-6 bg-white/5 hover:bg-white/10 text-white font-semibold text-sm px-6 py-2.5 rounded-lg">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
